// Considered: MenuAtom (`atoms/menu`, wraps PrimeReact Menu) — rejected as
// the WHOLE answer, though it is the atom this molecule is built ON. A plain
// menu item renders a label; this needs per-row supporting text naming a
// blast radius plus disabled-with-reason rows where the reason text is
// load-bearing UX, not a tooltip afterthought. `MenuAtom`'s `template` slot
// per item is what makes composing that rich a row possible without
// reaching into `primereact/menu` directly (Rule 2). See spec Reuse Audit
// (specs/per-level-context-filters.md).
import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import MenuAtom, { type MenuHandle, type MenuItem } from '../../atoms/menu/menu';
import type {
  FilterSaveTarget,
  FilterSaveTargetAvailability,
} from '../../../hooks/useLevelFilters';
import { SAVE_TARGET_REASON_KEY } from '../../../utils/filterSaveTargetReasons';
import './FilterSaveTargetMolecule.scss';

const LABEL_KEY: Record<FilterSaveTarget, string> = {
  session: 'columnFilters.saveTarget.session.label',
  user: 'columnFilters.saveTarget.user.label',
  view: 'columnFilters.saveTarget.view.label',
};

const BLAST_RADIUS_KEY: Record<FilterSaveTarget, string> = {
  session: 'columnFilters.saveTarget.session.blastRadius',
  user: 'columnFilters.saveTarget.user.blastRadius',
  view: 'columnFilters.saveTarget.view.blastRadius',
};

// Icon per target — communicates blast radius at a glance.
const TARGET_ICON: Record<FilterSaveTarget, string> = {
  session: 'pi-clock',
  user: 'pi-user',
  view: 'pi-bookmark',
};

export interface FilterSaveTargetMoleculeProps {
  /** Always all three targets, in blast-radius order — never hidden, per `useLevelFilters`. */
  targets: FilterSaveTargetAvailability[];
  onSelect: (target: FilterSaveTarget) => void;
  /** Fired alongside `onSelect` for the `context_filter_saved` analytics event. */
  contextSystemName?: string | null;
  levelId?: string;
  /**
   * Display name of the active view — interpolated into the "no_view_edit_permission"
   * reason so users know exactly which view they need edit on.
   */
  activeViewName?: string;
  /** Disables the trigger entirely — e.g. no draft filter to save. */
  disabled?: boolean;
  className?: string;
  /** `data-datum` id for the trigger button — lets a host tag it for prototype-parity capture. */
  triggerDatumId?: string;
  /**
   * Scopes the per-target row datum ids to `${datumScope}.button` — set by a
   * host (e.g. `uf-advanced`) whose own contract names the open menu's rows;
   * omitted for hosts (the column filter) that don't tag them.
   */
  datumScope?: string;
  /**
   * Explicit per-target row datum id, for a host whose contract names the
   * row with an id that isn't `${scope}.button` (e.g. `uf-column.button#25`,
   * `<CFSaveMenu> > each(targets)`). Takes precedence over `datumScope`.
   */
  rowDatumId?: string;
}

/** Render one save-target row with icon, title, blast-radius text, and (when
 *  applicable) a lock + italic reason for disabled rows. */
function SaveTargetRow({
  icon,
  label,
  support,
  disabled,
  isReason,
  datumId,
}: Readonly<{
  icon: string;
  label: string;
  support: string;
  disabled: boolean;
  isReason: boolean;
  datumId?: string;
}>) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={`filter-save-target__row${disabled ? ' filter-save-target__row--disabled' : ''}`}
      data-container="each(targets)"
      data-datum={datumId}
    >
      <span className="filter-save-target__row-left">
        <i className={`pi ${icon} filter-save-target__row-icon`} aria-hidden="true" />
      </span>
      <span className="filter-save-target__row-body">
        <span className="filter-save-target__row-label">
          {label}
          {disabled && <i className="pi pi-lock filter-save-target__row-lock" aria-hidden="true" />}
        </span>
        <span className={`filter-save-target__row-support${isReason ? ' filter-save-target__row-support--reason' : ''}`}>
          {support}
        </span>
      </span>
    </button>
  );
}

const FilterSaveTargetMolecule: React.FC<FilterSaveTargetMoleculeProps> = ({
  targets,
  onSelect,
  contextSystemName,
  levelId,
  activeViewName,
  disabled = false,
  className = '',
  triggerDatumId,
  datumScope,
  rowDatumId,
}) => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const menuRef = useRef<MenuHandle>(null);

  const handleSelect = useCallback(
    (target: FilterSaveTarget) => {
      onSelect(target);
      logAnalyticsEvent('context_filter_saved', undefined, {
        contextSystemName: contextSystemName ?? null,
        levelId: levelId ?? null,
        target,
      });
    },
    [onSelect, logAnalyticsEvent, contextSystemName, levelId]
  );

  const model: MenuItem[] = [
    // The three editable targets from useLevelFilters
    ...targets.map((availability) => {
      // A disabled row explains WHY; an enabled one states its blast radius.
      // The view-permission reason additionally names the view when known.
      let reasonText: string;
      if (!availability.reason) {
        reasonText = t(BLAST_RADIUS_KEY[availability.target]);
      } else if (availability.reason === 'no_view_edit_permission' && activeViewName) {
        reasonText = t('columnFilters.saveTarget.reason.noViewEditPermission', {
          viewName: activeViewName,
        });
      } else {
        reasonText = t(SAVE_TARGET_REASON_KEY[availability.reason]);
      }

      return {
        label: t(LABEL_KEY[availability.target]),
        disabled: availability.disabled,
        command: () => handleSelect(availability.target),
        template: () => (
          <SaveTargetRow
            icon={TARGET_ICON[availability.target]}
            label={t(LABEL_KEY[availability.target])}
            support={reasonText}
            disabled={availability.disabled}
            isReason={!!availability.reason}
            datumId={rowDatumId ?? (datumScope ? `${datumScope}.button` : undefined)}
          />
        ),
      };
    }),

    // 4th row: "Save to the context definition" — always disabled on this
    // surface (dev note #3). Editing a context-tier group and rewriting the
    // context definition are different jobs with different blast radii; the
    // route to relaxing it is "Edit in context".
    {
      label: t('columnFilters.saveTarget.context.label'),
      disabled: true,
      command: () => undefined,
      template: () => (
        <SaveTargetRow
          icon="pi-database"
          label={t('columnFilters.saveTarget.context.label')}
          support={t('columnFilters.saveTarget.context.disabledReason')}
          disabled
          isReason
        />
      ),
    },
  ];

  return (
    <div className={`filter-save-target ${className}`.trim()}>
      <ButtonAtom
        label={t('columnFilters.saveTarget.trigger')}
        icon="pi pi-chevron-up"
        iconPos="right"
        variant="primary"
        size="small"
        disabled={disabled}
        onClick={(e) => menuRef.current?.toggle(e)}
        data-datum={triggerDatumId}
      />
      <MenuAtom model={model} popup ref={menuRef} className="filter-save-target__menu" />
    </div>
  );
};

export default FilterSaveTargetMolecule;
